import { createContext, type ReactNode, useEffect, useMemo } from 'react';

import { appConfig } from '@ll-platform/frontend/config/app.config';
import { featureFlagOverrides } from '@ll-platform/frontend/config/featureFlags/consts';
import {
  defaultFeatureFlags,
  type FeatureFlagName,
  type FeatureFlags,
} from '@ll-platform/frontend/config/featureFlags/featureFlags';
import { FeatureFlagCustomContextProvider } from '@ll-platform/frontend/core/featureFlags/FeatureFlagCustomContextProvider';
import {
  LaunchDarklyProvider,
  useLaunchDarklyFeatureFlags,
} from '@ll-platform/frontend/core/featureFlags/useLaunchDarklyFeatureFlags';
import { useDeepCompareMemo } from '@ll-platform/frontend/utils/hooks/useDeepCompare';

const startTime = performance.now();

// For use outside react context. Not reactive!
export let StaticFeatureFlags: FeatureFlags = {
  ...defaultFeatureFlags,
  ...featureFlagOverrides,
};

type FeatureFlagProviderProps = {
  children: ReactNode;
};

export const FeatureFlagProvider = (props: FeatureFlagProviderProps) => {
  return (
    <FeatureFlagCustomContextProvider>
      <LaunchDarklyProvider>
        <AppFlagsProvider {...props} />
      </LaunchDarklyProvider>
    </FeatureFlagCustomContextProvider>
  );
};

type FeatureFlagsContext = {
  isLoading: boolean;
  flags: FeatureFlags;
};

export const AppFlagsContext = createContext<FeatureFlagsContext>({
  isLoading: true,
  flags: {
    ...defaultFeatureFlags,
    ...featureFlagOverrides,
  },
});
AppFlagsContext.displayName = 'AppFlagsContext';

const AppFlagsProvider = ({ children }: FeatureFlagProviderProps) => {
  const launchDarklyFlags = useLaunchDarklyFeatureFlags();
  const mergedFlags = useDeepCompareMemo(() => {
    const exposureTriggered = new Set<FeatureFlagName>();

    const hydrated = Object.keys(defaultFeatureFlags).reduce((acc, key) => {
      const value = {
        ...defaultFeatureFlags,
        ...launchDarklyFlags.query.data,
        ...featureFlagOverrides,
      }[key];

      Object.defineProperty(acc, key, {
        get: () => {
          if (exposureTriggered.has(key as FeatureFlagName)) {
            return value;
          }

          exposureTriggered.add(key as FeatureFlagName);
          if (appConfig.REACT_APP_FEATURE_FLAGS_DEBUG) {
            console.debug('[FeatureFlags] Flag exposure', key, value);
          }

          if (
            launchDarklyFlags.query.data &&
            key in launchDarklyFlags.query.data
          ) {
            launchDarklyFlags.trackExposure(key as FeatureFlagName);
          }

          return value;
        },
      });

      return acc;
    }, {} as FeatureFlags);

    return hydrated;
  }, [launchDarklyFlags]);

  StaticFeatureFlags = mergedFlags;

  useEffect(() => {
    if (
      appConfig.REACT_APP_FEATURE_FLAGS_DEBUG &&
      launchDarklyFlags.query.isSuccess
    ) {
      console.debug(
        `[FeatureFlags] LaunchDarkly loaded in ${Math.round(
          performance.now() - startTime,
        )}ms`,
        launchDarklyFlags.query.data,
      );
    }
  }, [launchDarklyFlags.query.isSuccess, launchDarklyFlags.query.data]);

  useEffect(() => {
    if (launchDarklyFlags.query.isError) {
      console.warn(
        '[FeatureFlags] LaunchDarkly failed to load. Fallback to defaults.',
        launchDarklyFlags.query.error,
      );
    }
  }, [launchDarklyFlags.query.isError, launchDarklyFlags.query.error]);

  const contextValue = useMemo(
    () => ({
      isLoading: launchDarklyFlags.query.isPending,
      flags: mergedFlags,
    }),
    [launchDarklyFlags.query.isPending, mergedFlags],
  );

  return (
    <AppFlagsContext.Provider value={contextValue}>
      {children}
    </AppFlagsContext.Provider>
  );
};
